/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { jsx } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

// Matches the bordered CopyLinkButton rendered beside it.
const styles = cssMap({
	trigger: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		boxSizing: 'border-box',
		width: '24px',
		height: '24px',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral.subtle'),
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
});

// Keeps trigger and item clicks out of the title's result-click handler; React bubbles portal
// events through the component tree, so the item needs it too.
const stopPropagation = (event: React.SyntheticEvent | Event) => event.stopPropagation();

export type CodeSearchPortalMenuProps = {
	href: string;
	/** Accessible name. */
	triggerLabel: string;
	triggerTooltip: string;
	openInCodeSearchLabel: string;
	testId?: string;
};

/** More-actions menu in a result title's hover group; its one item opens the Code Search Portal. */
export const CodeSearchPortalMenu = ({
	href,
	triggerLabel,
	triggerTooltip,
	openInCodeSearchLabel,
	testId,
}: CodeSearchPortalMenuProps): React.JSX.Element => (
	<DropdownMenu<HTMLButtonElement>
		// `isSelected` is an Atlaskit Button prop, not a DOM attribute.
		trigger={({ triggerRef, onClick, isSelected, ...triggerProps }) => (
			<Tooltip content={triggerTooltip} position="top">
				<Pressable
					{...triggerProps}
					ref={triggerRef}
					aria-label={triggerLabel}
					onClick={(event) => {
						stopPropagation(event);
						onClick?.(event);
					}}
					xcss={styles.trigger}
					testId={testId}
				>
					<ShowMoreHorizontalIcon label="" color={token('color.icon.subtle')} />
				</Pressable>
			</Tooltip>
		)}
		placement="bottom-start"
		testId={testId && `${testId}-menu`}
	>
		<DropdownItemGroup>
			<DropdownItem
				href={href}
				target="_blank"
				rel="noreferrer"
				// External site.
				UNSAFE_shouldDisableRouterLink
				onClick={stopPropagation}
				testId={testId && `${testId}-open`}
			>
				{openInCodeSearchLabel}
			</DropdownItem>
		</DropdownItemGroup>
	</DropdownMenu>
);
